/**
 * Tab on the first item of a list that directly follows another list nests the
 * whole list under the last item of that previous list (as in Notion), e.g.
 * bullet points under the last checkbox. Tab elsewhere in a list keeps its
 * usual behaviour (sink into the previous item of the same list).
 */
import { Extension } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { TextSelection, type EditorState, type Transaction } from '@tiptap/pm/state';

const LISTS = new Set(['bulletList', 'orderedList', 'taskList']);
const ITEMS = new Set(['listItem', 'taskItem']);

/** The transaction that nests the list around the cursor, or null if Tab means something else. */
export function nestIntoPreviousList(state: EditorState): Transaction | null {
  const { $from, from, to } = state.selection;
  let itemDepth = -1;
  for (let d = $from.depth; d > 0; d--) {
    if (ITEMS.has($from.node(d).type.name)) {
      itemDepth = d;
      break;
    }
  }
  if (itemDepth < 1) return null;
  const listDepth = itemDepth - 1;
  const list: PMNode = $from.node(listDepth);
  if (!LISTS.has(list.type.name) || $from.index(listDepth) !== 0) return null;
  const listPos = $from.before(listDepth);
  const previous = state.doc.resolve(listPos).nodeBefore;
  if (!previous || !LISTS.has(previous.type.name) || !previous.lastChild) return null;
  // The end of the previous list's last item: just before its closing token and the list's.
  const insertAt = listPos - 2;
  const tr = state.tr.delete(listPos, listPos + list.nodeSize).insert(insertAt, list);
  return tr.setSelection(TextSelection.create(tr.doc, from - 2, to - 2)).scrollIntoView();
}

export const ListIndent = Extension.create({
  name: 'listIndent',
  // Before the list items' own Tab handling.
  priority: 1000,
  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        const tr = nestIntoPreviousList(editor.state);
        if (!tr) return false;
        editor.view.dispatch(tr);
        return true;
      },
    };
  },
});
